import React from "react";
import { Navigate, useSearchParams } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { useResetPassword } from "../../hooks/useResetPassword";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import { CircleAlertIcon, EyeIcon, EyeOffIcon } from "lucide-react";
import { usePasswordInput } from "@/hooks/usePasswordInput";
import { Loader } from "@/components/Loader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { BrandMark } from "../Login/BrandMark";

type ResetPasswordForm = {
  password: string;
  confirmPassword: string;
};

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const resetPassword = useResetPassword();

  const { password, showPassword, handlePasswordChange, toggleShowPassword } = usePasswordInput();
  const {
    password: confirmPassword,
    showPassword: showConfirmPassword,
    handlePasswordChange: handleConfirmPasswordChange,
    toggleShowPassword: toggleShowConfirmPassword,
  } = usePasswordInput();

  const { handleSubmit } = useForm<ResetPasswordForm>();

  const hasExpired = React.useMemo(() => {
    if (!token) return false;
    const now = new Date().getTime();
    try {
      const decodedToken = jwtDecode(token);
      if (!decodedToken.exp) return false;
      return decodedToken.exp < now / 1000;
    } catch (e) {
      console.error("failed to decode jwt token: ", e);
      return true;
    }
  }, [token]);

  const onSubmit: SubmitHandler<ResetPasswordForm> = async () => {
    if (!token) {
      Sentry.captureEvent({ message: "token is empty when resetting password" });
      return null;
    }
    resetPassword.mutate({ token, newPassword: password });
  };

  if (!token) {
    return <Navigate to="/" replace />;
  }

  const canSubmit =
    !resetPassword.isPending &&
    password !== "" &&
    password.length >= 8 &&
    password === confirmPassword &&
    !hasExpired;

  return (
    <div className="flex min-h-dvh w-full items-center justify-center bg-background px-4 py-10 text-foreground">
      <form
        className="flex w-full max-w-[378px] flex-col gap-6 text-sm"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <BrandMark />
          <h1 className="text-xl font-title tracking-tight">Reset Your Password</h1>
          <p className="text-base text-muted-foreground">
            Choose a strong password and make sure to remember it.
          </p>
        </div>
        {hasExpired && (
          <Alert variant="destructive">
            <CircleAlertIcon />
            <AlertDescription className="text-destructive">
              This reset password link has expired. Request a new one in the mobile app.
            </AlertDescription>
          </Alert>
        )}
        {resetPassword.isError && (
          <Alert variant="destructive">
            <CircleAlertIcon />
            <AlertDescription className="text-destructive">
              We could not reset your password. Try again or request a new link in the mobile app.
            </AlertDescription>
          </Alert>
        )}
        <div className="surface-card flex flex-col gap-4 bg-card p-6 text-card-foreground">
          <PasswordField
            id="new-password"
            label="Password*"
            placeholder="Enter Password"
            value={password}
            onChange={handlePasswordChange}
            visible={showPassword}
            onToggleVisible={toggleShowPassword}
            error={
              password !== "" && password.length < 8
                ? "Password must be at least 8 characters long"
                : undefined
            }
          />
          <PasswordField
            id="confirm-password"
            label="Confirm Password*"
            placeholder="Repeat Password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            visible={showConfirmPassword}
            onToggleVisible={toggleShowConfirmPassword}
            error={
              confirmPassword !== "" && password !== confirmPassword
                ? "Passwords do not match"
                : undefined
            }
          />
          <Button type="submit" size="lg" className="mt-6 w-full" disabled={!canSubmit}>
            {resetPassword.isPending ? "Resetting..." : "Reset Password"}
          </Button>
        </div>
        {resetPassword.isPending && (
          <div className="pt-4">
            <Loader />
          </div>
        )}
      </form>
    </div>
  );
}

function PasswordField(props: {
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  visible: boolean;
  onToggleVisible: () => void;
  error?: string;
}) {
  const errorId = `${props.id}-error`;
  return (
    <Field>
      <FieldLabel htmlFor={props.id}>{props.label}</FieldLabel>
      <div className="relative">
        <Input
          id={props.id}
          type={props.visible ? "text" : "password"}
          className="h-9 pe-10"
          placeholder={props.placeholder}
          autoComplete="new-password"
          value={props.value}
          onChange={props.onChange}
          aria-invalid={props.error ? true : undefined}
          aria-describedby={props.error ? errorId : undefined}
        />
        <button
          type="button"
          onClick={props.onToggleVisible}
          className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={props.visible ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {props.visible ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
        </button>
      </div>
      {props.error && (
        <span id={errorId} className="text-xs text-destructive">
          {props.error}
        </span>
      )}
    </Field>
  );
}
